// ═══════════════════════════════════════════════════════════════
// SetPasswordView.jsx — Shown after invite or recovery link
// Path: src/views/SetPasswordView.jsx
//
// Detects that the user arrived via an invite/recovery link and
// prompts them to set (or reset) their password before entering
// the app. Calls supabase.auth.updateUser({ password }).
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { supabase } from "../lib/supabase";
import { FONT_SIZES, FONT_FAMILY, APP_NAME, ICON_SIZES } from "../constants/appConfig";
import { getTemplate } from "../constants/permissionDefaults";

export const SetPasswordView = () => {
  const { palette, navigate, userProfile, showMessage } = useApp();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pwChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const allChecksPassed = Object.values(pwChecks).every(Boolean);
  const passwordsMatch = password === confirm && confirm.length > 0;
  const canSubmit = allChecksPassed && passwordsMatch && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });
      if (updateError) throw updateError;

      showMessage(`Password set successfully — welcome to ${APP_NAME}!`);
      navigate("main");
    } catch (err) {
      setError(err.message || "Failed to set password. Please try again.");
    }
    setSaving(false);
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    paddingRight: 44,
    borderRadius: 10,
    fontSize: FONT_SIZES.md,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: palette.headerBg,
        fontFamily: FONT_FAMILY,
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: palette.cardBg,
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "28px 32px 20px",
            textAlign: "center",
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: `${palette.accent1}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
            }}
          >
            <Lock size={ICON_SIZES.xl} style={{ color: palette.accent1 }} />
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: FONT_SIZES.xxl,
              fontWeight: 700,
              color: palette.text1,
            }}
          >
            Set Your Password
          </h1>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: FONT_SIZES.sm,
              color: palette.text2,
              lineHeight: 1.5,
            }}
          >
            {userProfile?.full_name
              ? `Welcome, ${userProfile.full_name}! `
              : "Welcome! "}
            Create a password to secure your {APP_NAME} account.
          </p>
          {userProfile?.role && (() => {
            const template = getTemplate(userProfile.role);
            if (!template) return null;
            return (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 14px",
                  borderRadius: 8,
                  background: `${template.color}10`,
                  border: `1px solid ${template.color}25`,
                  textAlign: "left",
                }}
              >
                <div style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: template.color,
                  marginBottom: 2,
                }}>
                  Your role: {template.label}
                </div>
                <div style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text2,
                  lineHeight: 1.4,
                }}>
                  {template.description}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Form */}
        <div style={{ padding: "24px 32px 32px" }}>
          {/* Password field */}
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: palette.text2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 6,
              }}
            >
              New Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = palette.accent1;
                  e.target.style.boxShadow = `0 0 0 3px ${palette.accent1}15`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = palette.inputBorder;
                  e.target.style.boxShadow = "none";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSubmit) handleSubmit();
                }}
              />
              <button
                onClick={() => setShowPw(!showPw)}
                type="button"
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: palette.text3,
                  padding: 2,
                  display: "flex",
                }}
              >
                {showPw ? <EyeOff size={ICON_SIZES.md} /> : <Eye size={ICON_SIZES.md} />}
              </button>
            </div>
            {/* Password requirements checklist */}
            {password.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  marginTop: 8,
                }}
              >
                {[
                  { key: "length", label: "At least 8 characters" },
                  { key: "uppercase", label: "One uppercase letter" },
                  { key: "lowercase", label: "One lowercase letter" },
                  { key: "number", label: "One number" },
                  { key: "special", label: "One special character (!@#$...)" },
                ].map((rule) => (
                  <div
                    key={rule.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: FONT_SIZES.xs,
                      color: pwChecks[rule.key] ? "#16a34a" : "#d97706",
                      transition: "color 0.15s",
                    }}
                  >
                    {pwChecks[rule.key] ? (
                      <CheckCircle size={ICON_SIZES.xs} />
                    ) : (
                      <AlertCircle size={ICON_SIZES.xs} />
                    )}
                    {rule.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm field */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "block",
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: palette.text2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 6,
              }}
            >
              Confirm Password
            </label>
            <input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = palette.accent1;
                e.target.style.boxShadow = `0 0 0 3px ${palette.accent1}15`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = palette.inputBorder;
                e.target.style.boxShadow = "none";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) handleSubmit();
              }}
            />
            {confirm.length > 0 && !passwordsMatch && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                  fontSize: FONT_SIZES.xs,
                  color: "#dc2626",
                }}
              >
                <AlertCircle size={ICON_SIZES.xs} />
                Passwords don't match
              </div>
            )}
            {passwordsMatch && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                  fontSize: FONT_SIZES.xs,
                  color: "#16a34a",
                }}
              >
                <CheckCircle size={ICON_SIZES.xs} />
                Passwords match
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                color: "#991b1b",
                fontSize: FONT_SIZES.sm,
                marginBottom: 16,
              }}
            >
              <AlertCircle size={ICON_SIZES.sm} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: "100%",
              padding: "14px 20px",
              borderRadius: 10,
              border: "none",
              background: canSubmit ? palette.accent1 : palette.cardBorder,
              color: "#fff",
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
              cursor: canSubmit ? "pointer" : "default",
              opacity: canSubmit ? 1 : 0.5,
              transition: "all 0.2s",
            }}
          >
            {saving ? "Setting password..." : "Set Password & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};
